/**
 * RBAC Permission Registry (shared).
 *
 * Single source of truth for permissions: platform (users, roles, audit,
 * admin) + the Nord Living domain set in `studio-permissions.ts`.
 */

import type {
  PermissionCategory,
  PermissionDefinition,
} from "./permission-registry.types";
import { STUDIO_PERMISSIONS } from "./studio-permissions";

export const WILDCARD_PERMISSION = "*" as const;

export const NORD_LIVING_PERMISSIONS: readonly PermissionDefinition[] = [
  {
    code: "dashboard.view",
    name: "View Dashboard",
    description: "View the authenticated dashboard.",
    category: "dashboard",
  },

  {
    code: "users.view",
    name: "View Users",
    description: "View the users list.",
    category: "users",
  },
  {
    code: "users.create",
    name: "Create Users",
    description: "Create new users.",
    category: "users",
  },
  {
    code: "users.edit",
    name: "Edit Users",
    description: "Edit existing users.",
    category: "users",
  },
  {
    code: "users.delete",
    name: "Delete Users",
    description: "Delete users.",
    category: "users",
  },
  {
    code: "users.impersonate",
    name: "Impersonate Users",
    description: "Temporarily sign in as another (non-admin) user.",
    category: "users",
  },
  {
    code: "users.manage_roles",
    name: "Manage User Roles",
    description: "Assign roles to users.",
    category: "users",
  },

  {
    code: "roles.view",
    name: "View Roles",
    description: "View roles and permissions.",
    category: "roles",
  },
  {
    code: "roles.create",
    name: "Create Roles",
    description: "Create new roles.",
    category: "roles",
  },
  {
    code: "roles.edit",
    name: "Edit Roles",
    description: "Edit role details and permissions.",
    category: "roles",
  },
  {
    code: "roles.delete",
    name: "Delete Roles",
    description: "Delete roles.",
    category: "roles",
  },

  {
    code: "audit.view",
    name: "View Audit Logs",
    description: "View audit logs.",
    category: "audit",
  },
  {
    code: "audit.export",
    name: "Export Audit Logs",
    description: "Export audit data.",
    category: "audit",
  },
  {
    code: "audit.view_security",
    name: "View Security Logs",
    description: "View security-relevant audit logs.",
    category: "audit",
  },
  {
    code: "audit.delete",
    name: "Delete Audit Logs",
    description: "Delete audit logs.",
    category: "audit",
  },
  {
    code: "audit.retry_failed",
    name: "Retry Failed Requests",
    description: "Retry failed audited requests.",
    category: "audit",
  },

  {
    code: "admin.config",
    name: "Manage System Config",
    description: "Manage editable system configuration.",
    category: "admin",
  },
  {
    code: "admin.cache",
    name: "Manage Cache",
    description: "Manage application cache.",
    category: "admin",
  },
  {
    code: "admin.api_keys",
    name: "Manage API Keys",
    description: "Create, revoke, and manage API keys.",
    category: "admin",
  },
  {
    code: "admin.users",
    name: "Administer Users",
    description: "Full user management operations.",
    category: "admin",
  },
  {
    code: "docs.view",
    name: "View Documentation",
    description: "View internal project documentation.",
    category: "admin",
  },
  ...STUDIO_PERMISSIONS,
] as const;

export function getPermissionDefinitions(): readonly PermissionDefinition[] {
  assertUniquePermissionCodes(NORD_LIVING_PERMISSIONS);
  return NORD_LIVING_PERMISSIONS;
}

export function groupPermissionsByCategory(
  permissions: readonly PermissionDefinition[] = NORD_LIVING_PERMISSIONS,
): Record<string, PermissionDefinition[]> {
  const out: Record<string, PermissionDefinition[]> = {};
  for (const perm of permissions) {
    out[perm.category] ||= [];
    out[perm.category].push(perm);
  }
  return out;
}

export function listPermissionCategories(
  permissions: readonly PermissionDefinition[] = NORD_LIVING_PERMISSIONS,
): PermissionCategory[] {
  const categories = new Set<PermissionCategory>();
  for (const perm of permissions) categories.add(perm.category);
  return Array.from(categories).sort();
}

function assertUniquePermissionCodes(
  permissions: readonly PermissionDefinition[],
): void {
  const seen = new Set<string>();
  for (const perm of permissions) {
    if (seen.has(perm.code)) {
      throw new Error(`Duplicate permission code detected: ${perm.code}`);
    }
    seen.add(perm.code);
  }
}
